import { colors, hexToRgba, intl, ui } from '@beelday/common'
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'app-redux'
import { Config } from 'common/config'
import { brandImage } from 'organization/brand-image'
import { useOrganizationApi } from 'organization/organization-api'
import { setIsExpandableCompanyCard } from 'organization/redux'
import { useOrganization } from 'organization/use-organization-branding'
import { FC, useEffect } from 'react'
import { use100vh } from 'react-div-100vh'
import Exit from 'room/common/presentation/exit-icon'
import { selectTraining } from 'session-info/redux'

type Props = {
	onEnter: () => void
	/**
	 * The trainer, when the presentation window can open on the projector by
	 * itself: enter and open it with the same click (presenter mode).
	 */
	onEnterAndPresent?: () => void
}

/**
 * The door to a room the reader is already standing in.
 *
 * Every other way into this product is about crossing distance - check your
 * camera, join people who are elsewhere. This one is the opposite: everybody
 * is in the same building already, so the screen has one job, which is to
 * confirm *which room* and then get out of the way. That is why the venue is
 * the largest thing here and the training name is only a caption above it.
 *
 * The colour comes from the organization rather than from us - it is their
 * room and their session. `backgroundColor` and `fontColor` are always used as
 * the pair the organization chose, never mixed with our own, because an
 * arbitrary brand colour is only guaranteed to be legible against its own
 * partner.
 */

const rise = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to   { opacity: 1; transform: none; }
`

/**
 * Below 1024px the two halves stack - brand band on top, door underneath. The
 * band takes whatever height the door does not need, so the door sits at the
 * bottom with the Enter button under the thumb; on a screen too short for both
 * the page grows and scrolls instead of clipping.
 */
const Split = styled.div<{ viewportHeight: string }>`
	display: flex;
	flex-direction: column;
	width: 100%;
	min-height: ${({ viewportHeight }) => viewportHeight};
	background-color: ${colors.white};

	${ui.responsive.tablet} {
		flex-direction: row;
		width: 100vw;
		height: ${({ viewportHeight }) => viewportHeight};
		min-height: 0;
		overflow: hidden;
	}
`

const Panel = styled.div`
	flex: 0 0 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	padding: 28px 20px 20px;
	animation: ${rise} 0.45s ease-out both;

	${ui.responsive.smallTablet} {
		padding: 40px 40px 32px;
	}

	${ui.responsive.tablet} {
		flex: 1 1 56%;
		justify-content: center;
		padding: 64px clamp(40px, 6vw, 104px);
	}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

/** The organization's own field: their picture (`brandImage`) on their colour. */
const Field = styled.div`
	order: -1;
	flex: 1 0 200px;
	min-width: 0;
	position: relative;
	display: flex;
	align-items: flex-end;
	overflow: hidden;

	${ui.responsive.tablet} {
		order: 0;
		flex: 1 1 44%;
		min-height: 0;
	}
`

const FieldPhoto = styled.div`
	position: absolute;
	inset: 0;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`

/** A banner is shown whole on their colour, clear of the name below it. */
const FieldBanner = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px 20px 64px;

	img {
		display: block;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		border-radius: 12px;
	}

	${ui.responsive.tablet} {
		padding: 48px clamp(24px, 3vw, 48px) 112px;
	}
`

/** Carries the organization's name over the photo, in their own colours. */
const FieldName = styled.div`
	position: relative;
	width: 100%;
	padding: 16px 72px 16px 20px;
	font-size: 16px;
	font-weight: 500;
	line-height: 1.3;
	overflow-wrap: anywhere;

	${ui.responsive.tablet} {
		padding: 28px clamp(24px, 3vw, 48px);
		font-size: 20px;
	}
`

/**
 * The logo sits in the brand band below 1024px (`FieldLogo`) and heads the
 * door above it - one place per layout, so it is rendered in both and each
 * hides where the other shows.
 */
const Logo = styled.div`
	display: none;

	${ui.responsive.tablet} {
		display: block;
		margin-bottom: 40px;
	}
`

const FieldLogo = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;

	${ui.responsive.tablet} {
		display: none;
	}
`

const Eyebrow = styled.div`
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.18em;
	text-transform: uppercase;
	margin-bottom: 18px;
`

/**
 * Light and large on purpose. An address set this way reads as the sign
 * outside the room rather than as a heading in an app, which is exactly what
 * it is being used for here.
 */
const Venue = styled.h1`
	font-size: clamp(24px, 7vw, 40px);
	font-weight: 300;
	line-height: 1.1;
	letter-spacing: -0.02em;
	color: ${colors.blackish};
	margin: 0;
	overflow-wrap: anywhere;

	${ui.responsive.tablet} {
		font-size: clamp(32px, 3.4vw, 52px);
	}
`

const Rule = styled.div`
	width: 64px;
	height: 3px;
	border-radius: 2px;
	margin: 24px 0;

	${ui.responsive.tablet} {
		margin: 28px 0;
	}
`

const Subject = styled.div`
	font-size: 18px;
	font-weight: 500;
	color: ${colors.heather};
	margin-top: 14px;
	overflow-wrap: anywhere;
`

const Body = styled.p`
	font-size: 16px;
	line-height: 1.6;
	color: ${colors.blackish};
	max-width: 46ch;
	margin: 0 0 32px;

	${ui.responsive.tablet} {
		margin: 0 0 40px;
	}
`

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
`

const ExitCorner = styled.div`
	position: absolute;
	top: 20px;
	right: 24px;
	z-index: 2;
`

const PreparationScreenInPerson: FC<Props> = ({
	onEnter,
	onEnterAndPresent,
}) => {
	const containerHeight = use100vh()
	const dispatch = useDispatch()
	const training = useSelector(state => selectTraining(state.sessionInfo))
	const organization = useOrganization(training?.organizationId)
	const branding = organization?.branding

	// What the online `PreparationScreen` does on the way in: every room this
	// screen leads to - projector, laptop, phone - shows the company in full.
	useEffect(() => {
		dispatch(setIsExpandableCompanyCard(false))
	}, [dispatch])

	const brand = branding?.backgroundColor || colors.indigoBlue
	const brandInk = branding?.fontColor || colors.white
	const image = brandImage(branding)
	const organizationApi = useOrganizationApi()

	// Without a venue the training name has to carry the screen, so it moves up
	// into the display slot rather than leaving an empty one.
	const venue = training?.venue
	const headline = venue || training?.subject

	return (
		<Split
			viewportHeight={containerHeight ? `${containerHeight}px` : '100vh'}
			style={{ position: 'relative' }}
		>
			<ExitCorner>
				<Exit
					color={brandInk}
					backgroundColor={brand}
					trainerId={training?.upperEchelon.userId}
				/>
			</ExitCorner>

			<Panel>
				{branding?.logoId ? (
					<Logo>
						<ui.CompanyLogo
							size="64px"
							logoId={branding.logoId}
							userApiUrl={Config.USER_API_URL}
							style={{ borderRadius: '14px' }}
						/>
					</Logo>
				) : null}

				<Eyebrow style={{ color: brand }}>
					<intl.Translate>in_person.preparation.eyebrow</intl.Translate>
				</Eyebrow>

				<Venue>{headline}</Venue>
				{venue && training?.subject ? (
					<Subject>{training.subject}</Subject>
				) : null}

				<Rule style={{ backgroundColor: brand }} />

				<Body>
					<intl.Translate>in_person.preparation.no_devices</intl.Translate>
				</Body>

				<Actions>
					<ui.ButtonPrimary
						onClick={onEnter}
						data-testid="session.in_person.enter"
						css={css`
							background-color: ${brand};
							color: ${brandInk};
							width: 100%;
							height: 52px;

							${ui.responsive.smallTablet} {
								width: auto;
								min-width: 220px;
							}
						`}
					>
						<intl.Translate>in_person.preparation.enter</intl.Translate>
					</ui.ButtonPrimary>
					{onEnterAndPresent ? (
						<ui.ButtonMinor
							onClick={onEnterAndPresent}
							data-testid="session.in_person.enter_and_present"
							css={css`
								width: 100%;
								height: 52px;

								${ui.responsive.smallTablet} {
									width: auto;
									min-width: 220px;
								}
							`}
						>
							<intl.Translate>
								in_person.preparation.enter_and_present
							</intl.Translate>
						</ui.ButtonMinor>
					) : null}
				</Actions>
			</Panel>

			<Field style={{ backgroundColor: brand }}>
				{image?.kind === 'photo' ? (
					<FieldPhoto>
						<ui.CompanyHeroPhoto
							width="100%"
							height="100%"
							userApiUrl={Config.USER_API_URL}
							photoId={image.fileId}
							style={{ width: '100%', height: '100%' }}
						/>
					</FieldPhoto>
				) : image?.kind === 'banner' ? (
					<FieldBanner data-testid="session.in_person.banner">
						<img src={organizationApi.getLogoUrl(image.fileId)} alt="" />
					</FieldBanner>
				) : null}
				{/* A banner is the brand's own artwork; the logo stays off it. */}
				{branding?.logoId && image?.kind !== 'banner' ? (
					<FieldLogo>
						<ui.CompanyLogo
							size="80px"
							logoId={branding.logoId}
							userApiUrl={Config.USER_API_URL}
							style={{
								borderRadius: '18px',
								boxShadow: `0 8px 24px ${hexToRgba(colors.black, 0.18)}`,
							}}
						/>
					</FieldLogo>
				) : null}
				{organization?.name ? (
					<FieldName
						style={{
							color: brandInk,
							backgroundColor: image ? brand : 'transparent',
						}}
					>
						{organization.name}
					</FieldName>
				) : null}
			</Field>
		</Split>
	)
}

export default PreparationScreenInPerson
